import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { authService } from '@/services/authService';

import type { LoginPayload, StaffUser } from '@/types/auth.types';

interface AuthState {
  // State
  isAuthenticated: boolean;
  accessToken: string | null;
  user: StaffUser | null;
  isLoading: boolean;
  error: string | null;
  hasHydrated: boolean;

  // Actions
  login: (payload: LoginPayload) => Promise<{ ok: true } | { ok: false; error: string }>;
  validateSession: () => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setHasHydrated: (hydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      isAuthenticated: false,
      accessToken: null,
      user: null,
      isLoading: false,
      error: null,
      hasHydrated: false,

      login: async (payload) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(payload);

          set({
            isAuthenticated: true,
            accessToken: response.data.accessToken,
            user: response.data.user,
            isLoading: false,
          });

          return { ok: true };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Login failed';
          set({ isLoading: false, error: message });
          return { ok: false, error: message };
        }
      },

      validateSession: async () => {
        try {
          const response = await authService.getStaffSession();
          set({ user: response.data });
        } catch {
          set({
            isAuthenticated: false,
            accessToken: null,
            user: null,
            error: null,
          });
        }
      },

      logout: () => {
        set({
          isAuthenticated: false,
          accessToken: null,
          user: null,
          error: null,
        });
      },

      clearError: () => set({ error: null }),

      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'kent-hr-auth',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);
